import type {PlacementIdentifier} from '@packages/utilities/box';
import type {
  ApplyOffset,
  AnimatePopoverConfig,
  PopoverProps,
} from '@packages/systems/design-system/Popover';
// eslint-disable-next-line webflow/no-dangerous-imports
import type {
  MessageType,
  PopoverSizeType,
  ImageType,
  VideoType,
  HotspotType,
  CanvasType,
} from '@packages/systems/ui/shared-ui/CalloutPopover';
import {type ComponentType} from 'react';
import {Component} from '@packages/systems/wfdl';
import {Status, Modes, Errors} from './constants';
import {type Theme} from '@packages/utilities/styled';
import type {DesignerStores} from '@packages/systems/designer/flux';

type Style = Record<any, any>;

export type ButtonType = {
  /**
   * — "prev" renders a "Previous" button that on click steps back to the
   *   previous step
   * — "next" renders a "Next" button that on click steps forward to the next
   *   step
   * — "custom" allows rendering your own custom UI where the buttons would've
   *   rendered. Used in conjunction with `render`
   */
  type: 'prev' | 'next' | 'custom';
  /**
   * Allows to configure the button text. Only relevant with 'prev' and 'next' types.
   * Defaults to "Previous" and "Next" respectively.
   */
  // @ts-expect-error - TS2724 - 'React' has no exported member named 'Element'. Did you mean 'CElement'?
  text?: string | React.Element;
  /**
   * Allows to configure in-line css styles.
   */
  style?: Style | ((arg1: Theme) => Style);
  /**
   * Allows rendering your own custom UI where the buttons would've rendered.
   * Used in conjunction with `type: "custom"`
   */
  render?: ComponentType<any>;
  /** Optional id for Cypress test automation */
  ['data-automation-id']?: string;
};

type Selector = string;
type IframeId = string;

type PopoverConfig = {
  /**
   * The mask is an overlay on top of the main document body. The popover rests
   * on top of the mask.
   * — "full" renders an overlay; UI underneath are _not_ interactive
   * — "transparent" renders a transparent mask; UI underneath are _not_ interactive
   * — "none" renders no mask; UI underneath are interactive
   *
   * Defaults to "full"
   */
  mask?: PopoverProps['showMask'];
  /**
   * — "anchor" renders a blue overlay over the anchor element and makes it
   *   interactive, eg allowing click/hover events to go through
   * — "border" renders a blue border over the anchor element and makes it
   *   interactive, eg allowing click/hover events to go through
   * — "none" does not highlight the anchor with an overlay or border. If the mask
   *   is full or transparent, the anchor will be non-interactive
   *
   *   Defaults to "none"
   */
  hotspot?: HotspotType;
  /**
   * The canvas is the main document body. This applies when there is a mask/overlay.
   * — "static" makes the canvas underneath the mask transparent but NOT interactive
   * — "interactive" makes the canvas underneath the mask transparent AND interactive
   * — "scroll" same as static, except the canvas is scrollable
   * — "none" ensures the canvas is not transparent and not interactive. This is the
   *   default and puts the canvas underneath the mask
   *
   *   Defaults to "none"
   */
  canvas?: CanvasType;
  /** Determines the location of the popover placement if space is available */
  placement?: PlacementIdentifier;
  /** Optional popover offset config */
  applyOffset?: ApplyOffset;
  /**
   * Optional animation config. If set, the popover translates and fades
   * into the position with the AnimatePopoverConfig values
   */
  animate?: AnimatePopoverConfig;
  /** Determines the width, padding, and font size. Defaults to "MEDIUM" */
  size?: PopoverSizeType;
  /** Override the popover max-width in pixels. Takes precedence over size-based width. */
  width?: number;
};

/** Popover ID that maps to a UI element in the Designer */
type PopoverId = string;

/** Tutorial ID that maps to a tutorial */
export type TutorialId = string;

/** Step ID that maps to a step in a tutorial */
export type StepId = string;

/**
 * Valid CSS selector used to wrap the tutorial popover over any HTML
 * element on the canvas.
 */
export type PopoverDOMNodeSelector = {
  iframe?: IframeId;
  selector: Selector;
};

// prettier-ignore
export type DragDestinationSelector = (PopoverDOMNodeSelector) & {
 position: /** Place as a sibling before the anchor node */
 'before'
 /** Place as a sibling after the anchor node */ | 'after'
 /** Place as the last child of the anchor node */ | 'prepend'
 /** Place as the first child of the anchor node */ | 'append';
 /**
  * Highlights the anchor node with an overlay and centers the drag indicator.
  * Can also accept a selector to specify what node to highlight (e.g. parent)
  *
  * TODO: this could be refactored when GA'ing to be a more robust implementation.
  */
 __EXPERIMENTAL__highlight?: boolean | PopoverDOMNodeSelector;
};

export type Step = {
  ui?: {
    id:
      | PopoverId
      | PopoverDOMNodeSelector
      | ((state: DesignerStores) => PopoverDOMNodeSelector);
    type?: 'popover';
    popoverConfig?: PopoverConfig;
    title?: React.ReactNode;
    content: React.ReactNode;
    buttonFooterText?: React.ReactNode;
    btnLeft?: ButtonType;
    btnRight?: ButtonType;
    highlightedMessage?: MessageType;
    image?: ImageType;
    video?: VideoType;
    dragDestinationSelector?: DragDestinationSelector;
    showCloseBtn?: boolean;
    hasImageInsideContent?: boolean;
  };
  /**
   * A side effect to fire when starting the step
   */
  onEnter?: (arg1: Dispatch) => void;
  /**
   * A side effect to fire after completing the step
   */
  onExit?: (arg1: Dispatch) => void;
  /**
   * A predicate function that will be registered to the flux dispatcher once
   * it is added to the `pending` state. The flux dispatcher will invoke
   * `shouldComplete` on _every_ action, and on returning true, the engine
   * will dispatch an action to move the step forward, or complete a step item.
   *
   * You may want to treat this like a reducer, and only compute logic for the
   * specific action you want the user to take. Other techniques include
   * memoizing computations, or reusing other store reducers, etc.
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  readonly shouldComplete?: <A extends any>(
    action: A,
    nextComponents: Component.ComponentMap
  ) => boolean | (typeof Errors)[keyof typeof Errors];
  /**
   * Similar to `shouldComplete`, except instead of moving forward this undoes
   * the previous step. In other words, for Sequence tutorials it moves to the
   * previous step, and in Checklist tutorials it unchecks the previous step.
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  readonly shouldReverse?: <A extends any>(action: A) => boolean;
  /**
   * Used to persist a tutorial state so a user can resume where they left off
   * after coming back to the tutorial
   */
  persist?: true;
  /**
   * Optional notification to display when a user tries to complete a step
   * incorrectly. The message should generally nudge them towards the right
   * action.
   */
  error?: {
    title?: string;
    message: string;
  };
  /**
   * Optional. Used to conditionally select what the next step should be
   * based on the global state.
   */
  nextStep?: <S extends GlobalState>(globalState: S) => StepId;
};

type TutorialConfigOption = boolean | ((arg1: null | StepId) => boolean);

export type _TutorialConfig = {
  /** The tutorial identifier, eg `nux` or `videoIntro` */
  id: TutorialId;
  /**
   * Determines if the onboarding should progress sequentially or in any
   * order (aka a checklist). This is set once and never changed.
   */
  readonly mode: (typeof Modes)[keyof typeof Modes];
  /**
   * Maintains the current status of the onboarding flow, eg whether it has not
   * started (idle), is in progress (active), if it completed (ended), or was
   * dismissed.
   */
  status: (typeof Status)[keyof typeof Status];
  /** The pre-defined steps to take for a given onboarding experience. */
  steps: Partial<Record<StepId, Step>>;
  /** A list containing the steps that the user completed */
  stepsCompleted: Array<StepId>;
  /** The current actionable step. Applies only to `sequence` types. */
  currentStepId: null | StepId;
  /** The number of times this tutorial has been resumed */
  resumedCount: number;
  /** Enables a tutorial to be exited based on if a callback function is provided. */
  exitTutorial?: (arg1: TutorialConfig | null, arg2: Dispatch) => void;
  /** Allows a tutorial to be restarted if user views the project again before tutorial is completed. */
  canResume?: boolean;
  /**
   * Options to enable/disable certain Designer functionalities or hide
   * specific UI while the designer is active. Accepts either a boolean or a
   * function to conditionally evaluate the value based on the currentStepId.
   */
  opts: {
    /** Hides collapsed sections/panels across designer when the tutorial is active. */
    hideCollapsedSections?: TutorialConfigOption;
    /** Disable keyboard shortcuts when the tutorial is active. */
    disableShortcuts?: TutorialConfigOption;
    /** Hides blue/orange highlights in style panel UI when the tutorial is active. */
    hideStylePanelHighlights?: TutorialConfigOption;
    /** Hides breadcrumbs from selected elements on canvas when the tutorial is active. */
    hideBreadcrumbs?: TutorialConfigOption;
    /** Hides mini settings when adding new elements on canvas when the tutorial is active. */
    hideMiniSettings?: TutorialConfigOption;
    /** Hides the breakpoint button on-hover tooltips when the tutorial is active. */
    hideBreakpointButtonTooltips?: TutorialConfigOption;
    /** Hides AddBar help tooltips when the tutorial is active. */
    hideAddBarTooltips?: TutorialConfigOption;
    /** Hides the color picker in the StylePanel color inputs, when the tutorial is active. */
    hideColorPicker?: TutorialConfigOption;
    /** Force the navigator to be on the right panel while dragging a node on
     * canvas, when the tutorial is active */
    forceNavigatorRightWhileDragging?: TutorialConfigOption;
    /** Hides the TopBar when the tutorial is active. */
    hideTopBar?: TutorialConfigOption;
  };
};

type GlobalState = any;

export type Pending = {
  /**
   * The current tutorial step(s) that have yet to be completed.
   * Sequence tutorials will have only 1 step at a given time
   * as the tutorial progresses.
   * Checklist tutorials can have any number of steps, since
   * these steps can complete in any order.
   */
  steps: Partial<Record<StepId, Step>> | null;
  /** The active tutorial ID — `null` if none is active. */
  tutorial: TutorialId | null;
  /**
   * Keeps track of global flux state for a sequence tutorial.
   * This is typically used in conjunction with a step that is
   * configured to have an error notification. For example, dragging
   * an element to the wrong spot will revert flux global state to
   * the snapshot state prior to the step, allowing users to try again.
   */
  globalStateStepSnapshot: Partial<Record<StepId, GlobalState>>;
  /**
   * Keeps track of current errors for a given step in a sequence tutorial.
   * This is typically used in conjunction with the globalStateStepSnapshot
   * (see description above), and also used to hook into the NotificationStore
   * system to render the error message.
   *
   * `null` represents no errors.
   */
  error: null | {
    type: (typeof Errors)[keyof typeof Errors];
    stepId: StepId;
  };
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export type Dispatch = <A extends any>(action: A) => void;

export type _State = {
  /**
   * A map of all available tutorial configurations.
   */
  tutorials: Partial<Record<TutorialId, TutorialConfig>>;
  /**
   * `pending` state keeps track of the active tutorial, its
   * current steps, global flux state snapshot, and any error states.
   */
  pending: Pending;
};

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type State = _State;
// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type TutorialConfig = _TutorialConfig;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const applyOpaque: <NonOpaqueType extends any, OpaqueType>(
  arg1: NonOpaqueType
  // @ts-expect-error - TS2322 - Type 'NonOpaqueType' is not assignable to type 'OpaqueType'.
) => OpaqueType = (a) => a;

export type CreateTutorialConfigParams = {
  id: TutorialConfig['id'];
  mode: TutorialConfig['mode'];
  steps: TutorialConfig['steps'];
  status?: TutorialConfig['status'];
  stepsCompleted?: TutorialConfig['stepsCompleted'];
  currentStepId?: TutorialConfig['currentStepId'];
  resumedCount?: TutorialConfig['resumedCount'];
  exitTutorial?: TutorialConfig['exitTutorial'];
  canResume?: TutorialConfig['canResume'];
  opts?: TutorialConfig['opts'];
};
